import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ActionIcon, Flex, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Add } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useBulkReorderPriorityStatusesMutation, useGetPriorityStatusesQuery } from "../../graphql/graphql";
import { DeletePriorityStatusModal } from "./DeletePriorityStatusModal";
import { PriorityStatusModal } from "./PriorityStatusModal";
import { SortablePriorityStatus } from "./SortablePriorityStatus";
export const PriorityStatusView = ({ opened }) => {
    // State
    const [priorityStatus, setPriorityStatus] = useState(null);
    const [priorityStatusState, setPriorityStatusState] = useState([]);
    // Mutation for reordering priority statuses
    const [_, bulkReorderPriorityStatuses] = useBulkReorderPriorityStatusesMutation();
    // Modals
    const [priorityStatusModalOpened, { open: priorityStatusModalOpen, close: priorityStatusModalClose }] = useDisclosure(false);
    const [priorityStatusDeleteModalOpened, { open: priorityStatusDeleteModalOpen, close: priorityStatusDeleteModalClose }] = useDisclosure(false);
    // Query to get the priority statuses
    const [{ data, fetching }] = useGetPriorityStatusesQuery({
        pause: !opened
    });
    const priorityStatuses = useMemo(() => (data?.priorityStatuses?.priorityStatuses ?? []).filter((status) => status !== null), [data?.priorityStatuses?.priorityStatuses]);
    // Update the priority status state when the priority statuses are fetched
    useEffect(() => {
        setPriorityStatusState(priorityStatuses);
    }, [priorityStatuses]);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
    }));
    const deduplicatedColorHexes = [
        ...new Set(priorityStatusState.map((status) => status?.colorHex))
    ];
    const handleClickAndDrag = (event) => {
        const { active, over } = event;
        if (!active || !over)
            return;
        if (active.id === over.id) {
            handleClick(String(active.id));
        }
        else {
            handleDragEnd(active, over);
        }
    };
    const handleClick = (id) => {
        const priorityStatus = priorityStatusState.find((status) => status?.priorityStatusId === id);
        if (!priorityStatus)
            return;
        setPriorityStatus(priorityStatus);
        priorityStatusModalOpen();
    };
    const handleDragEnd = async (active, over) => {
        setPriorityStatusState((items) => {
            const oldIndex = items.findIndex((item) => item?.priorityStatusId === active.id);
            const newIndex = items.findIndex((item) => item?.priorityStatusId === over.id);
            const newItems = arrayMove(items, oldIndex, newIndex);
            // Create reindexed array with the new order
            const reindexedStatuses = newItems
                .map((status, index) => {
                // If the status is not mutable, don't reindex it
                if (!status || !status.isMutable)
                    return null;
                return {
                    entityId: status.priorityStatusId,
                    sortOrder: index
                };
            })
                .filter((status) => status !== null);
            bulkReorderPriorityStatuses({
                input: reindexedStatuses
            });
            return newItems;
        });
    };
    const handleClickEdit = (priorityStatus) => {
        if (!priorityStatus) {
            setPriorityStatus(null);
            priorityStatusModalOpen();
        }
        else {
            setPriorityStatus(priorityStatus);
            priorityStatusModalOpen();
        }
    };
    const handleClickDelete = (priorityStatus) => {
        setPriorityStatus(priorityStatus);
        priorityStatusDeleteModalOpen();
    };
    return (_jsxs(Stack, { children: [_jsxs(Flex, { justify: "space-between", align: "center", style: { marginTop: "1rem", marginBottom: "1rem" }, children: [_jsx(Text, { variant: "body-strong", children: "Drag to reorder referral stages" }), _jsx(ActionIcon, { variant: "filled", onClick: () => handleClickEdit(), children: _jsx(SvgIcon, { component: Add, fontSize: "inherit" }) })] }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleClickAndDrag, children: _jsx(SortableContext, { items: priorityStatusState
                        .map((status) => status?.priorityStatusId ?? null)
                        .filter((status) => status !== null), strategy: verticalListSortingStrategy, children: priorityStatusState.map((priorityStatus) => (_jsx(SortablePriorityStatus, { priorityStatus: priorityStatus, onClickEdit: handleClickEdit, onClickDelete: handleClickDelete }, priorityStatus?.priorityStatusId))) }) }), _jsx(PriorityStatusModal, { priorityStatus: priorityStatus, sortOrder: priorityStatusState.length, existingColorHexes: deduplicatedColorHexes, opened: priorityStatusModalOpened, close: priorityStatusModalClose }), priorityStatus && (_jsx(DeletePriorityStatusModal, { priorityStatus: priorityStatus, opened: priorityStatusDeleteModalOpened, close: priorityStatusDeleteModalClose }))] }));
};
